/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';

type RemoveResourceAssignmentModalProps = BasicModalProps<object> & {
    selectedRegionName: string;
    selectedResourceName: string;
};

const messages = defineMessages({
    modalTitle: {
        id: 'modelDeploy.removeResourceAssignmentModal.modalTitle',
        defaultMessage: 'Remove resource assignment'
    },
    modalDescription1: {
        id: 'modelDeploy.removeResourceAssignmentModal.modalDescription1',
        defaultMessage: 'This action will unassign resource'
    },
    modalDescription2: {
        id: 'modelDeploy.removeResourceAssignmentModal.modalDescription2',
        defaultMessage: ' from the region'
    },
    modalDescription3: {
        id: 'modelDeploy.removeResourceAssignmentModal.modalDescription3',
        defaultMessage: ' immediately.'
    },
    modalDescription4: {
        id: 'modelDeploy.removeResourceAssignmentModal.modalDescription4',
        defaultMessage:
            'This will remove your existing deployments from this region and you will not be able to make prediction calls using this resource for any existing deployments.'
    },
    confirmResourceName: {
        id: 'modelDeploy.removeResourceAssignmentModal.confirmResourceName',
        defaultMessage: 'Please type the resource name to confirm:'
    },
    resourceNamePlaceHolder: {
        id: 'modelDeploy.removeResourceAssignmentModal.resourceNamePlaceHolder',
        defaultMessage: 'Resource name'
    },
    removeButton: {
        id: 'modelDeploy.removeResourceAssignmentModal.removeButton',
        defaultMessage: 'Remove'
    }
});

export const RemoveResourceAssignmentModal = observer((props: RemoveResourceAssignmentModalProps) => {
    const { hidden, disabled, onSubmit, onDismiss, selectedRegionName, selectedResourceName } = props;

    const { localize } = useLocalization();

    const [confirmedResourceName, setConfirmedResourcName] = React.useState<string>(null);

    const handleSubmit = (data?: Object) => {
        onSubmit(data);
        setConfirmedResourcName(null);
    };

    const handleDismiss = () => {
        setConfirmedResourcName(null);
        onDismiss();
    };

    return (
        <BasicModal
            hidden={hidden}
            onSubmit={handleSubmit}
            disabled={disabled}
            onDismiss={handleDismiss}
            doneButtonText={localize(messages.removeButton)}
            dialogContentProps={{
                title: localize(messages.modalTitle)
            }}
            valid={confirmedResourceName === selectedResourceName}>
            <Stack tokens={{ childrenGap: '20px' }}>
                <LuisText>
                    {`${localize(messages.modalDescription1)} `}
                    <LuisText styles={{ root: { fontWeight: 'bold' } }}>{selectedResourceName}</LuisText>
                    {`${localize(messages.modalDescription2)} `}
                    <LuisText styles={{ root: { fontWeight: 'bold' } }}>{selectedRegionName}</LuisText>
                    {localize(messages.modalDescription3)}
                </LuisText>

                <LuisText>{localize(messages.modalDescription4)}</LuisText>

                <LuisTextField
                    value={confirmedResourceName}
                    placeholder={localize(messages.resourceNamePlaceHolder)}
                    onChange={(_, name) => {
                        setConfirmedResourcName(name);
                    }}
                    styles={{ fieldGroup: { marginTop: '10px' } }}
                    onRenderLabel={() => (
                        <LuisText>
                            {localize(messages.confirmResourceName)}{' '}
                            <LuisText styles={{ root: { fontWeight: 'bold' } }}>{selectedResourceName}</LuisText>
                        </LuisText>
                    )}
                />
            </Stack>
        </BasicModal>
    );
});
